import { useMemo } from "react";
import { suspectSpansFromCharConfidences } from "@capture/shared";
import { HighlightedText } from "./HighlightedText";
import type { FieldState } from "../state/types";

/**
 * ADR-013 — suspect-character readout under the active field: the OCR read
 * with sub-threshold characters tinted to draw the keyer's eye. SECONDARY
 * signal by contract — it gates nothing and must stay visually subordinate
 * to the candidate-diff panel (FieldInput mounts the arbitration panel
 * INSTEAD of this when a disagreement exists). Engines self-report
 * confidence poorly when hallucinating, so the copy never implies that
 * un-tinted characters are verified.
 */
export function SuspectCharacters({
  charConfidences,
}: {
  charConfidences: FieldState["charConfidences"];
}) {
  const spans = useMemo(
    () =>
      charConfidences
        ? suspectSpansFromCharConfidences(charConfidences.text, charConfidences.spans)
        : [],
    [charConfidences],
  );

  if (!charConfidences || spans.length === 0) return null;

  return (
    <div
      className="suspect-characters"
      role="note"
      aria-label="Low-confidence characters in the OCR read — check them against the image"
    >
      <span className="suspect-label">OCR READ</span>
      <span className="suspect-text">
        <HighlightedText text={charConfidences.text} spans={spans} />
      </span>
      <span className="suspect-hint" aria-hidden="true">
        low-confidence characters — check against the image
      </span>

      <style>{`
        .suspect-characters {
          margin-top: var(--space-1);
          padding: var(--space-1) var(--space-2);
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border);
          border-left: 3px solid var(--color-warning);
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          display: flex;
          align-items: baseline;
          gap: var(--space-2);
          flex-wrap: wrap;
        }
        .suspect-label {
          flex-shrink: 0;
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          color: var(--color-text-muted);
        }
        .suspect-text {
          font-family: "SF Mono", "Fira Code", Consolas, monospace;
          white-space: pre-wrap;
        }
        /* Deliberately distinct from the candidate-diff mark (solid warning
           tint, bold): suspect chars get a lighter wash + dotted underline —
           readable as "look here", not "these differ". */
        .suspect-text mark {
          background: color-mix(in srgb, var(--color-warning) 14%, transparent);
          color: inherit;
          border-radius: 2px;
          padding: 0 1px;
          text-decoration: underline dotted var(--color-warning) 1.5px;
          text-underline-offset: 2px;
        }
        .suspect-hint {
          font-size: 0.6rem;
          color: var(--color-text-muted);
        }
      `}</style>
    </div>
  );
}
